/**
 * Support tickets for customers, resellers and Rapid Fusion staff.
 */

import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authFetch, authPost } from '../../utils/api';
import TicketFilters from './desk/TicketFilters';
import TicketTable from './desk/TicketTable';
import BulkBar from './desk/BulkBar';
import SavedViews from './desk/SavedViews';
import RatingPrompt from './desk/RatingPrompt';
import AgreementPanel from './AgreementPanel';
import './Support.css';

const DRAFT_KEY = 'rf-ticket-draft';
const EMPTY_FILTERS = {
  q: '',
  audience: '',
  type: '',
  product: '',
  orgId: '',
  requester: '',
  assignee: '',
  priority: '',
  tag: '',
  from: '',
  to: '',
  rating: '',
  overdue: '',
};

function ageLabel(iso) {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return '';
  const hours = Math.floor(ms / 3600000);
  if (hours < 1) return 'under an hour';
  if (hours < 48) return `${hours} hours`;
  return `${Math.floor(hours / 24)} days`;
}

async function readBody(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || 'Something went wrong. Try again.');
    error.status = res.status;
    throw error;
  }
  return data;
}

function statusLabel(meta, id) {
  const found = (meta?.statuses || []).find((item) => item.id === id);
  return found ? found.label : id;
}

function SupportPage({ userRole }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [queue, setQueue] = useState('open');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState('');
  const [dir, setDir] = useState('desc');
  const [counts, setCounts] = useState({});
  const [facets, setFacets] = useState({ orgs: [], requesters: [], noOrg: false });
  const [selected, setSelected] = useState([]);
  const [views, setViews] = useState({ presets: [], views: [] });
  const [notice, setNotice] = useState('');
  const [reload, setReload] = useState(0);
  const [newType, setNewType] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({});
  const [showAgreements, setShowAgreements] = useState(false);
  const [coverOpen, setCoverOpen] = useState(false);
  const [coverRevision, setCoverRevision] = useState(0);
  const staff = Boolean(meta?.viewer?.staff || ['admin', 'sales', 'user'].includes(userRole));
  const showNew = searchParams.get('new') === '1';
  const ticketId = searchParams.get('ticket') || '';

  useEffect(() => {
    let cancelled = false;
    authFetch('/api/tickets/meta')
      .then(readBody)
      .then((data) => { if (!cancelled) setMeta(data); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (showNew) {
      try {
        const saved = sessionStorage.getItem(DRAFT_KEY);
        const parsed = saved ? JSON.parse(saved) : {};
        setDraft(parsed);
        setNewType(parsed.type || '');
      } catch (err) {
        setDraft({});
        setNewType('');
      }
    }
  }, [showNew]);

  useEffect(() => {
    if (!staff) return undefined;
    let cancelled = false;
    authFetch('/api/tickets/views')
      .then(readBody)
      .then((data) => { if (!cancelled) setViews(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [staff]);

  useEffect(() => {
    if (!meta || showNew || ticketId) return undefined;
    let cancelled = false;
    const query = new URLSearchParams();
    if (staff) {
      query.set('queue', queue);
      Object.entries(filters).forEach(([key, value]) => {
        if (value) query.set(key, value);
      });
      if (sort) {
        query.set('sort', sort);
        query.set('dir', dir);
      }
    }
    const suffix = query.toString() ? `?${query.toString()}` : '';
    authFetch(`/api/tickets${suffix}`)
      .then(readBody)
      .then((data) => {
        if (cancelled) return;
        setTickets(data.items || []);
        setCounts(data.counts || {});
        setFacets(data.facets || { orgs: [], requesters: [], noOrg: false });
      })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [meta, staff, queue, showNew, ticketId, filters, sort, dir, reload]);

  useEffect(() => {
    setCoverOpen(false);
  }, [ticketId]);

  useEffect(() => {
    if (!ticketId) {
      setTicket(null);
      return undefined;
    }
    let cancelled = false;
    authFetch(`/api/tickets/${encodeURIComponent(ticketId)}`)
      .then(readBody)
      .then((data) => { if (!cancelled) setTicket(data); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [ticketId, coverRevision]);

  function applyView(view) {
    const next = { ...EMPTY_FILTERS };
    const source = (view && view.filters) || {};
    Object.keys(EMPTY_FILTERS).forEach((key) => {
      if (source[key]) next[key] = source[key];
    });
    if (source.queue) setQueue(source.queue);
    setSort(source.sort || '');
    setDir(source.dir || 'desc');
    setFilters(next);
    setSelected([]);
  }

  async function saveView(name) {
    const next = [
      ...(views.views || []),
      {
        id: `v${Date.now()}`,
        name,
        filters: { ...filters, queue, sort, dir },
      },
    ];
    setBusy(true);
    setError('');
    try {
      const saved = await readBody(await authFetch('/api/tickets/views', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ views: next }),
      }));
      setViews(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteView(id) {
    const next = (views.views || []).filter((view) => view.id !== id);
    setBusy(true);
    try {
      const saved = await readBody(await authFetch('/api/tickets/views', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ views: next }),
      }));
      setViews(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  function toggleSort(key) {
    if (sort === key) setDir((current) => (current === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(key);
      setDir(key === 'priority' || key === 'subject' || key === 'requester' ? 'asc' : 'desc');
    }
  }

  async function runBulk(action, value) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await readBody(await authPost('/api/tickets/bulk', { ids: selected, action, value }));
      const rows = result.results || [];
      const changed = rows.filter((row) => row.result === 'changed').length;
      const skipped = rows.filter((row) => row.result === 'skipped').length;
      const failed = rows.filter((row) => row.result === 'error');
      setNotice(`${changed} updated, ${skipped} already set${failed.length ? `, ${failed.length} failed` : ''}.`);
      if (failed.length) setError(failed[0].error);
      setSelected([]);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function downloadExport(path, filename) {
    setError('');
    try {
      const res = await authFetch(path);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Could not download that file.');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  }

  function listQuery() {
    const query = new URLSearchParams();
    query.set('queue', queue);
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    if (sort) {
      query.set('sort', sort);
      query.set('dir', dir);
    }
    return query.toString();
  }

  function openList() {
    setError('');
    setSearchParams({});
  }

  function openNew() {
    setError('');
    sessionStorage.removeItem(DRAFT_KEY);
    setDraft({});
    setSearchParams({ new: '1' });
  }

  async function uploadFiles(id, updatedAt, fileList) {
    if (!fileList || !fileList.length) return null;
    const form = new FormData();
    form.append('expectedUpdatedAt', updatedAt);
    Array.from(fileList).forEach((file) => form.append('files', file));
    const res = await authFetch(`/api/tickets/${encodeURIComponent(id)}/files`, { method: 'POST', body: form });
    return readBody(res);
  }

  async function saveHandover(event) {
    event.preventDefault();
    if (!ticket || !ticket.handover) return;
    const form = event.target;
    const body = { org: ticket.handover.org };
    (ticket.handover.missing || []).forEach((key) => {
      if (form[key] && form[key].value) body[key] = form[key].value;
    });
    setBusy(true);
    setError('');
    try {
      const saved = await readBody(await authPost('/api/tickets/handover', body));
      setTicket({ ...ticket, handover: saved });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function createTicket(event) {
    event.preventDefault();
    const form = event.target;
    setBusy(true);
    setError('');
    try {
      const created = await readBody(await authPost('/api/tickets', {
        subject: form.subject.value,
        description: form.description.value,
        category: form.category.value,
        type: form.type.value,
        product: form.product ? form.product.value : '',
        machineStopped: form.machineStopped.checked,
        source: draft && draft.source === 'bob' ? 'bob' : 'portal',
        pageUrl: (draft && draft.pageUrl) || window.location.pathname,
        bobContext: (draft && draft.bobContext) || '',
      }));
      const withFiles = await uploadFiles(created.id, created.updatedAt, form.files.files);
      sessionStorage.removeItem(DRAFT_KEY);
      setTicket(withFiles || created);
      setSearchParams({ ticket: created.id });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function sendReply(event) {
    event.preventDefault();
    const form = event.target;
    setBusy(true);
    setError('');
    try {
      const result = await readBody(await authPost(`/api/tickets/${encodeURIComponent(ticket.id)}/reply`, {
        body: form.body.value,
        internal: Boolean(form.internal && form.internal.checked),
        expectedUpdatedAt: ticket.updatedAt,
      }));
      const next = result.ticket || result;
      const withFiles = await uploadFiles(next.id, next.updatedAt, form.files.files);
      setTicket(withFiles || next);
      if (next.id !== ticket.id) setSearchParams({ ticket: next.id });
      form.reset();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteTicket(item) {
    if (!item || !window.confirm(`Delete ${item.id}? This cannot be undone.`)) return;
    setBusy(true);
    setError('');
    try {
      await readBody(await authFetch(`/api/tickets/${encodeURIComponent(item.id)}`, { method: 'DELETE' }));
      setTickets((current) => current.filter((row) => row.id !== item.id));
      if (ticketId === item.id) openList();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function changeTicket(path, body) {
    setBusy(true);
    setError('');
    try {
      const data = await readBody(await authPost(`/api/tickets/${encodeURIComponent(ticket.id)}${path}`, {
        ...body,
        expectedUpdatedAt: ticket.updatedAt,
      }));
      setTicket(data.ticket || data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="support-page">
      <div className="support-head">
        <h1>Support</h1>
        {!ticketId && !showNew && (
          <span className="support-head-actions">
            {staff && <Link className="rf-btn rf-btn-outline" to="/support/insights">Insights</Link>}
            {staff && (
              <button type="button" className="rf-btn rf-btn-outline" onClick={() => setShowAgreements((open) => !open)}>
                Service agreements
              </button>
            )}
            {userRole === 'admin' && <Link className="rf-btn rf-btn-outline" to="/support/settings">Settings</Link>}
            <button type="button" className="rf-btn rf-btn-primary" onClick={openNew}>New ticket</button>
          </span>
        )}
      </div>

      {(ticketId || showNew) && (
        <button type="button" className="rf-btn rf-btn-outline support-back" onClick={openList}>
          <i className="fas fa-arrow-left" aria-hidden="true"></i>
          Back to all tickets
        </button>
      )}

      {staff && meta && !meta.emailConfigured && (
        <p className="support-note">Email is not connected yet. Tickets still save here.</p>
      )}
      {error && <p className="support-error">{error}</p>}
      {staff && showAgreements && !ticketId && !showNew && (
        <AgreementPanel onSaved={() => setCoverRevision((n) => n + 1)} />
      )}

      {showNew && (
        <form className="support-form rf-card rf-card-body" onSubmit={createTicket}>
          <label className="rf-label" htmlFor="support-type-new">What kind of request is this?</label>
          <select id="support-type-new" name="type" className="rf-input" required value={newType} onChange={(event) => setNewType(event.target.value)}>
            <option value="">Choose</option>
            {(meta?.types || []).map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
          {(newType === 'bug' || newType === 'feature') && (
            <>
              <label className="rf-label" htmlFor="support-product-new">Which product?</label>
              <select id="support-product-new" name="product" className="rf-input" defaultValue={(draft && draft.product) || ''}>
                <option value="">Not sure</option>
                {(meta?.products || []).map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
            </>
          )}
          <label className="rf-label" htmlFor="support-category">What is this about?</label>
          <select id="support-category" name="category" className="rf-input" defaultValue={(draft && draft.category) || 'other'}>
            {(meta?.categories || [{ id: 'other', label: 'Other' }]).map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
          <label className="rf-label" htmlFor="support-subject">Subject</label>
          <input id="support-subject" name="subject" className="rf-input" required maxLength={160} defaultValue={(draft && draft.subject) || ''} />
          <label className="rf-label" htmlFor="support-description">What do you need help with?</label>
          <textarea id="support-description" name="description" className="rf-input" rows={6} required defaultValue={(draft && draft.description) || ''} />
          <label className="support-check">
            <input type="checkbox" name="machineStopped" defaultChecked={Boolean(draft && draft.machineStopped)} />
            My machine is stopped
          </label>
          <label className="rf-label" htmlFor="support-files">Files, if you have them</label>
          <input id="support-files" name="files" type="file" multiple />
          <button type="submit" className="rf-btn rf-btn-primary" disabled={busy}>Open ticket</button>
        </form>
      )}

      {!showNew && !ticketId && (
        <>
          {staff && (
            <div className="support-queues" role="tablist" aria-label="Ticket queues">
              {(meta?.queues || []).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={queue === item.id}
                  className={`support-queue ${queue === item.id ? 'active' : ''}`}
                  onClick={() => { setQueue(item.id); setSelected([]); }}
                >
                  {item.label}{counts[item.id] != null ? ` (${counts[item.id]})` : ''}
                </button>
              ))}
            </div>
          )}
          {staff && (
            <>
              <div className="support-desk-tools">
                <SavedViews pack={views} busy={busy} onApply={applyView} onSave={saveView} onDelete={deleteView} />
                <span className="support-tool-actions">
                  <button
                    type="button"
                    className="rf-btn rf-btn-ghost"
                    onClick={() => downloadExport(`/api/tickets/export.csv?${listQuery()}`, 'support-tickets.csv')}
                  >
                    Download spreadsheet
                  </button>
                  <button
                    type="button"
                    className="rf-btn rf-btn-ghost"
                    onClick={() => downloadExport('/api/tickets/export/cursor', 'support-cursor-export.zip')}
                  >
                    Export bugs and features
                  </button>
                  <button type="button" className="rf-btn rf-btn-ghost" onClick={() => { setFilters(EMPTY_FILTERS); setSelected([]); }}>
                    Clear filters
                  </button>
                </span>
              </div>
              <TicketFilters filters={filters} facets={facets} meta={meta} onChange={(next) => { setFilters(next); setSelected([]); }} />
              {notice && <p className="support-note">{notice}</p>}
              <BulkBar count={selected.length} meta={meta} busy={busy} onApply={runBulk} />
              <TicketTable
                tickets={tickets}
                meta={meta}
                selected={selected}
                sort={sort}
                dir={dir}
                busy={busy}
                canDelete={userRole === 'admin'}
                onToggle={(id) => setSelected((current) => (
                  current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
                ))}
                onToggleAll={(on) => setSelected(on ? tickets.map((item) => item.id) : [])}
                onOpen={(id) => setSearchParams({ ticket: id })}
                onSort={toggleSort}
                onDelete={deleteTicket}
              />
            </>
          )}
          {!staff && (
            <div className="support-list">
              {tickets.length === 0 && <p className="support-meta">No tickets here.</p>}
              {tickets.map((item) => (
                <div key={item.id} className="support-row">
                  <button
                    type="button"
                    className="support-row-open"
                    onClick={() => setSearchParams({ ticket: item.id })}
                  >
                    <span>
                      <strong>{item.subject}</strong>
                      <br />
                      <small>{item.id} · {statusLabel(meta, item.status)}</small>
                    </span>
                    {item.waitingForReply && (
                      <small>Waiting for a first reply, {ageLabel(item.createdAt)}</small>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {ticket && (
        <div className="support-thread">
          <p className="support-meta">
            {ticket.id} · {statusLabel(meta, ticket.status)} · {ticket.requester?.name}
            {ticket.org?.name ? ` · ${ticket.org.name}` : ''}
            {staff && ticket.audience ? ` · ${statusLabel({ statuses: meta?.audiences }, ticket.audience)}` : ''}
            {ticket.type ? ` · ${statusLabel({ statuses: meta?.types }, ticket.type)}` : ''}
            {ticket.rating?.score ? ` · Rated ${ticket.rating.score}` : ''}
            {ticket.waitingForReply ? ` · Waiting for a first reply, ${ageLabel(ticket.createdAt)}` : ''}
          </p>
          {ticket.machine && (
            <p className="support-meta">
              {ticket.machine.platform || 'Machine'}
              {ticket.machine.generation ? ` ${ticket.machine.generation}` : ''}
              {ticket.machine.site ? ` · ${ticket.machine.site}` : ''}
              {ticket.machine.installedLabel ? ` · ${ticket.machine.installedLabel}` : ''}
              {ticket.machine.ageMonths != null ? ` · ${ticket.machine.ageMonths} months from install` : ''}
            </p>
          )}
          {staff && ticket.handover && (
            <form className="support-filters" onSubmit={saveHandover}>
              <span className="support-meta">Handover for {ticket.handover.org}. A date already filled in stays.</span>
              {[['fatOn', 'FAT'], ['satOn', 'SAT'], ['trainingOn', 'Training']].map(([key, label]) => (
                <label key={key} className="rf-label">
                  {label}
                  {ticket.handover[key]
                    ? <span>{ticket.handover[`${key}Label`]}</span>
                    : <input className="rf-input" type="date" name={key} />}
                </label>
              ))}
              {(ticket.handover.missing || []).length > 0 && (
                <button type="submit" className="rf-btn rf-btn-secondary" disabled={busy}>Save dates</button>
              )}
            </form>
          )}
          {staff && ticket.autoReply && !ticket.autoReply.ok && (
            <p className="support-error">Bob could not send the automatic first reply. The customer has not heard from us yet.</p>
          )}
          {staff && ticket.cover && (
            <div className="support-note">
              <p>{ticket.cover.label}</p>
              {(ticket.cover.lines || []).slice(1).map((line, index) => <p key={`${index}-${line}`}>{line}</p>)}
              <button type="button" className="rf-btn rf-btn-secondary" onClick={() => setCoverOpen((open) => !open)}>
                {coverOpen ? 'Hide agreement' : 'Review agreement'}
              </button>
              {coverOpen && (
                <AgreementPanel
                  orgId={ticket.cover.orgId || ticket.org?.id}
                  orgName={ticket.cover.orgName || ticket.org?.name}
                  onSaved={() => setCoverRevision((n) => n + 1)}
                />
              )}
            </div>
          )}
          {staff && ticket.context?.repeat && (
            <p className="support-note">
              {`${ticket.context.repeat.count} ${ticket.context.repeat.tag} tickets at ${ticket.context.repeat.org} in 12 months`}
              {ticket.context.repeat.lastId ? ` (last ${ticket.context.repeat.lastId})` : ''}.
            </p>
          )}
          {staff && ticket.context?.machineLine && <p className="support-meta">{ticket.context.machineLine}</p>}
          {staff && ticket.context?.otherSites > 0 && (
            <p className="support-meta">{`${ticket.context.otherSites} other sites have seen this.`}</p>
          )}
          {(ticket.suggestions || []).length > 0 && (
            <div className="support-note">
              <strong>These answers may already cover it</strong>
              <ul>
                {ticket.suggestions.map((item) => <li key={item.id}>{item.question}</li>)}
              </ul>
            </div>
          )}
          {ticket.machineChoices && ticket.machineChoices.length > 1 && (
            <label className="rf-label">
              Which machine?
              <select
                className="rf-input"
                defaultValue=""
                onChange={(event) => event.target.value && changeTicket('/machine', { machineId: event.target.value })}
              >
                <option value="">Choose</option>
                {ticket.machineChoices.map((machine) => (
                  <option key={machine.id} value={machine.id}>
                    {[machine.site, machine.platform, machine.generation, (machine.serials || [])[0]].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </select>
            </label>
          )}
          {staff && (
            <div className="support-tags">
              {(ticket.tags || []).length === 0 && <span className="support-meta">No tags yet.</span>}
              {(ticket.tags || []).map((tag) => (
                <button
                  key={tag.slug}
                  type="button"
                  className="support-tag"
                  onClick={() => changeTicket('/tags', { remove: tag.slug })}
                  disabled={busy}
                >
                  {tag.slug}{tag.source === 'ai' ? ' · suggested' : ''} ×
                </button>
              ))}
              <input
                className="rf-input support-tag-add"
                list="support-tag-list"
                placeholder="Add a tag"
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  const slug = event.currentTarget.value.trim().toLowerCase();
                  if (!slug) return;
                  changeTicket('/tags', { add: slug });
                  event.currentTarget.value = '';
                }}
              />
              <datalist id="support-tag-list">
                {(meta?.tags || []).map((item) => <option key={item.slug} value={item.slug}>{item.label}</option>)}
              </datalist>
              {(ticket.followers || []).length > 0 && (
                <p className="support-meta">Also notified: {ticket.followers.map((person) => person.name).join(', ')}</p>
              )}
            </div>
          )}
          {ticket.articles.map((article) => (
            <article key={article.id} className={`support-article ${article.kind === 'note' ? 'note' : ''}`}>
              <strong>{article.author?.name || 'Someone'}</strong>
              {article.kind === 'note' ? ' · Internal note' : ''}
              {article.flagged ? ` · ${article.flagged}` : ''}
              <p>{article.body}</p>
              {(article.attachments || []).map((file) => (
                <p key={file.id}>
                  <a href={`/api/tickets/${ticket.id}/files/${file.id}?token=${encodeURIComponent(localStorage.getItem('authToken') || '')}`}>{file.filename}</a>
                </p>
              ))}
            </article>
          ))}

          {ticket.canRate && (
            <RatingPrompt
              current={ticket.rating}
              busy={busy}
              onRate={(score, comment) => changeTicket('/rating', { score, comment })}
            />
          )}
          {staff && (
            <div className="support-actions">
              <label className="rf-label" htmlFor="support-ticket-type">Type</label>
              <select
                id="support-ticket-type"
                className="rf-input"
                value={ticket.type || ''}
                onChange={(event) => changeTicket('/type', { type: event.target.value, product: ticket.product || '' })}
                disabled={busy}
              >
                <option value="">Not set</option>
                {(meta?.types || []).map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
              <label className="rf-label" htmlFor="support-ticket-product">Product</label>
              <select
                id="support-ticket-product"
                className="rf-input"
                value={ticket.product || ''}
                onChange={(event) => changeTicket('/type', { type: ticket.type || '', product: event.target.value })}
                disabled={busy}
              >
                <option value="">Not set</option>
                {(meta?.products || []).map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
              <label className="rf-label" htmlFor="support-assignee">Assign</label>
              <select
                id="support-assignee"
                className="rf-input"
                value={ticket.assignee?.email || ''}
                onChange={(event) => changeTicket('/assign', { email: event.target.value })}
                disabled={busy}
              >
                <option value="">Unassigned</option>
                {(meta?.staff || []).map((person) => (
                  <option key={person.email} value={person.email}>{person.name}</option>
                ))}
              </select>
              <label className="rf-label" htmlFor="support-priority">Priority</label>
              <select
                id="support-priority"
                className="rf-input"
                value={ticket.priority}
                onChange={(event) => changeTicket('/priority', { priority: event.target.value })}
                disabled={busy}
              >
                {(meta?.priorities || []).map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
              {userRole === 'admin' && (
                <button type="button" className="rf-btn rf-btn-danger" disabled={busy} onClick={() => deleteTicket(ticket)}>
                  Delete ticket
                </button>
              )}
              {['open', 'pending', 'solved', 'closed'].map((status) => (
                <button
                  key={status}
                  type="button"
                  className="rf-btn rf-btn-secondary"
                  disabled={busy || ticket.status === status}
                  onClick={() => changeTicket('/status', { status })}
                >
                  {statusLabel(meta, status)}
                </button>
              ))}
            </div>
          )}

          {!staff && ticket.status !== 'closed' && ticket.status !== 'solved' && (
            <button type="button" className="rf-btn rf-btn-secondary" disabled={busy} onClick={() => changeTicket('/status', { status: 'solved' })}>
              Mark as solved
            </button>
          )}
          {!staff && ticket.status === 'solved' && (
            <button type="button" className="rf-btn rf-btn-secondary" disabled={busy} onClick={() => changeTicket('/status', { status: 'open' })}>
              Reopen
            </button>
          )}

          <form className="support-form" onSubmit={sendReply}>
            <label className="rf-label" htmlFor="support-reply">
              {ticket.status === 'closed' ? 'A reply opens a new ticket' : 'Reply'}
            </label>
            <textarea id="support-reply" name="body" className="rf-input" rows={4} required />
            {staff && (
              <label className="support-check">
                <input type="checkbox" name="internal" />
                Internal note. The customer does not see this.
              </label>
            )}
            <input name="files" type="file" multiple />
            <button type="submit" className="rf-btn rf-btn-primary" disabled={busy}>Send</button>
          </form>
        </div>
      )}
    </section>
  );
}

export default SupportPage;
